import { PropsWithChildren, useEffect } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  getGraphQLErrorsByStatusCode,
  GraphQLStatusCodes,
} from '@/graphql/errors';
import { deletedHouseholdIdsVar } from '@/graphql/reactiveVars';
import { getHouseholdIntegration } from '@/modules/household/householdIntegrations';
import { useSubBrand } from '@/modules/tenant/TenantDetailsContext/useSubBrand';
import { useMostRecentlyViewedHouseholds } from '@/modules/user/hooks/useMostRecentlyViewedHouseholds';
import { HouseholdWorkflowTrackersProvider } from '@/modules/workflowTrackers/contexts';
import { StateCode } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import {
  GetClientDetails_HouseholdFragment,
  useGetClientDetailsQuery,
} from './graphql/GetClientDetails.generated';
import { useRefreshIntegrationDataForHouseholdMutation } from './graphql/RefreshIntegrationDataForHousehold.generated';
import {
  useHouseholdDetailsContext,
  useHouseholdDetailsUpdaterContext,
} from './householdDetails.context';

export const HouseholdDetailsAwareRoute = ({
  householdId,
  children,
}: PropsWithChildren<{ householdId: string | null }>) => {
  const { showFeedback } = useFeedback();
  const { addMostRecentlyViewedHousehold } = useMostRecentlyViewedHouseholds();

  const { householdIntegrationLink } = useHouseholdDetailsContext();
  const {
    setLoading,
    setHouseholdId,
    setCreatedAt,
    setDisplayName,
    setIsPrivateHousehold,
    setPrimaryClients,
    setDefaultFirstToDieClientId,
    setPossibleGrantors,
    setPossibleTrustees,
    setPossibleBeneficiaries,
    setEntities,
    setIsTwoClientHousehold,
    setHouseholdIntegrationLink,
    setBillableKind,
    setShowGenerateV2SuggestionsPrompt,
    setRecentClients,
    setHouseholdNotFound,
    setDocuments,
  } = useHouseholdDetailsUpdaterContext();

  const {
    data: clientData,
    error,
    loading,
  } = useGetClientDetailsQuery({
    variables: { householdId: householdId! },
    skip: !householdId,
  });

  const [refreshIntegrationDataForHousehold] =
    useRefreshIntegrationDataForHouseholdMutation();

  const household =
    clientData?.household as GetClientDetails_HouseholdFragment | null;
  const recentClients = clientData?.recentClients;

  useEffect(() => {
    const householdId = household?.id;
    const displayName = household?.displayName;

    if (householdId && displayName) {
      void addMostRecentlyViewedHousehold(
        householdId,
        displayName,
        household.private
      );
    }
  }, [
    addMostRecentlyViewedHousehold,
    household?.displayName,
    household?.id,
    household?.private,
  ]);

  useEffect(() => {
    // If the household is linked to an integration, send a request to the backend to
    // *potentially* refetch the integration data. As of Oct 2025, this is only used for
    // the eMoney integration, and it only runs at most once per 24 hours.
    if (householdId && Boolean(householdIntegrationLink)) {
      void refreshIntegrationDataForHousehold({
        variables: { householdId, force: false },
      });
    }
  }, [
    householdId,
    householdIntegrationLink,
    refreshIntegrationDataForHousehold,
  ]);

  useEffect(() => {
    // if we've already run through this effect once and marked the household as deleted,
    // read the variable directly. this resolves a bug where if we're just reliant on the error
    // being present, if a user hits the back button to get to a household-aware page for a deleted household,
    // we wouldn't detect that the household was deleted and would incorrectly attempt to render the page normally
    const isHouseholdIdMarkedAsDeleted = deletedHouseholdIdsVar().includes(
      householdId ?? ''
    );
    if (isHouseholdIdMarkedAsDeleted) {
      return setHouseholdNotFound(true);
    }

    // if we're seeing an error when loading the client, check to see if it's a not found error
    // and mark the household as deleted if so
    if (error) {
      const householdNotFound =
        getGraphQLErrorsByStatusCode(error, GraphQLStatusCodes.NOT_FOUND)
          .length > 0;

      setHouseholdNotFound(householdNotFound);
      if (householdNotFound && householdId) {
        const currentDeletedHouseholdIds = deletedHouseholdIdsVar();
        void deletedHouseholdIdsVar([
          ...currentDeletedHouseholdIds,
          householdId,
        ]);
      } else {
        showFeedback(
          'Failed to load details for this client. Please refresh the page and try again.'
        );
      }
    }
  }, [
    error,
    setHouseholdNotFound,
    clientData?.household,
    householdId,
    showFeedback,
  ]);

  useEffect(() => {
    const entities = getNodes(household?.entities);

    setLoading(loading);
    setHouseholdId(householdId ?? null);
    setCreatedAt(household?.createdAt ?? null);
    setDisplayName(household?.displayName ?? null);
    setIsPrivateHousehold(household?.private ?? false);
    setPrimaryClients(
      household?.possiblePrimaryClients
        // Sort by createdAt to ensure stable ordering — the first-created
        // client is always [0]. Downstream consumers (e.g., DeathOrderSelector)
        // rely on array position to determine primary vs secondary death order.
        // NOTE: This assumes the first-created client is the "primary" death
        // order client. If the business rule changes, this sort (or the
        // backend query) should be updated accordingly.
        .slice()
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() ||
            a.id.localeCompare(b.id)
        )
        .map((c) => ({
          ...c,
          stateCode: c.address?.stateCode as StateCode | undefined,
        })) ?? null
    );
    setDefaultFirstToDieClientId(
      household?.defaultFirstToDieClient?.id ?? null
    );
    setPossibleGrantors(household?.possibleGrantors ?? null);
    setPossibleTrustees(
      household
        ? {
            clients: household.clientProfiles ?? [],
            organizations: household.clientOrganizations ?? [],
          }
        : null
    );
    setPossibleBeneficiaries(
      household
        ? {
            clients: household.clientProfiles ?? [],
            organizations: household.clientOrganizations ?? [],
            entities,
          }
        : null
    );
    setEntities(household ? entities : null);
    setIsTwoClientHousehold(household?.possiblePrimaryClients.length === 2);
    setBillableKind(household?.billableKind ?? null);
    setShowGenerateV2SuggestionsPrompt(
      household?.showGenerateV2SuggestionsPrompt ?? false
    );
    setDocuments(
      household?.documents?.map((d) => ({
        id: d.id,
        name: d.name,
        type: d.type,
        file: { id: d.file.id },
      })) ?? []
    );

    const integration = getHouseholdIntegration(household);
    setHouseholdIntegrationLink(integration);

    setRecentClients(recentClients ?? []);

    return () => {
      setHouseholdId(null);
      setCreatedAt(null);
      setDisplayName(null);
      setIsPrivateHousehold(false);
      setPrimaryClients(null);
      setDefaultFirstToDieClientId(null);
      setPossibleGrantors(null);
      setEntities(null);
      setIsTwoClientHousehold(false);
      setHouseholdIntegrationLink(null);
      setBillableKind(null);
      setShowGenerateV2SuggestionsPrompt(false);
      setRecentClients([]);
      setHouseholdNotFound(false);
      setDocuments([]);
    };
  }, [
    loading,
    household,
    recentClients,
    setDisplayName,
    setCreatedAt,
    setDocuments,
    setHouseholdId,
    setHouseholdIntegrationLink,
    setHouseholdNotFound,
    setIsTwoClientHousehold,
    setIsPrivateHousehold,
    setPossibleBeneficiaries,
    setPossibleGrantors,
    setPossibleTrustees,
    setPrimaryClients,
    setDefaultFirstToDieClientId,
    setBillableKind,
    setShowGenerateV2SuggestionsPrompt,
    setRecentClients,
    setLoading,
    householdId,
    setEntities,
  ]);

  useSubBrand(household?.subBrand?.id ?? null);

  return (
    <HouseholdWorkflowTrackersProvider
      // Wait to subscribe to workflow trackers until the initial household
      // details resolve so we do not fetch trackers for an inaccessible household.
      householdID={household?.id === householdId ? household.id : null}
    >
      {children}
    </HouseholdWorkflowTrackersProvider>
  );
};
